import ErrorIcon from '@mdi/svg/svg/alert-circle.svg?react';
import BookmarkIcon from '@mdi/svg/svg/bookmark.svg?react';
import PastScheduledIcon from '@mdi/svg/svg/calendar-check.svg?react';
import ScheduledIcon from '@mdi/svg/svg/calendar-clock.svg?react';
import CanceledScheduledIcon from '@mdi/svg/svg/calendar-remove.svg?react';
import CarIcon from '@mdi/svg/svg/car.svg?react';
import RobotIcon from '@mdi/svg/svg/creation.svg?react';
import TransactionIcon from '@mdi/svg/svg/currency-usd.svg?react';
import DownloadIcon from '@mdi/svg/svg/download.svg?react';
import AgentIcon from '@mdi/svg/svg/face-agent.svg?react';
import HealthIcon from '@mdi/svg/svg/hospital-box.svg?react';
import DeliveryIcon from '@mdi/svg/svg/moped.svg?react';
import PackageIcon from '@mdi/svg/svg/package-variant-closed.svg?react';
import MotionIcon from '@mdi/svg/svg/run-fast.svg?react';
import OpenAlarmIcon from '@mdi/svg/svg/shield-alert.svg?react';
import AlarmIcon from '@mdi/svg/svg/shield-outline.svg?react';
import { type IEvent } from 'clients/PlatformApi';
/**
 * @param props.event - The event to get an icon for.
 * @param props.size - If specified, the desired height of the icon in pixels
 */
import { type LocaleDefinition } from '@solinkcorp/locale';
import {
  EVENT_SUBTYPE_AI,
  EVENT_SUBTYPE_ALARM,
  EVENT_SUBTYPE_DOWNLOAD,
  EVENT_TYPE_BOOKMARK,
  EVENT_TYPE_MOTION,
  EVENT_TYPE_TRANSACTION,
} from '@solinkcorp/vd-common';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import {
  AGENT_SUBTYPE,
  DELIVERY_EVENT_SUBTYPE,
  DWELL_TIME_SUBTYPE,
  EVENT_STATUS_CLOSED,
  EVENT_STATUS_OPEN,
  EVENT_SUBTYPE_SCHEDULED,
  EXIT_DOOR_OBSTRUCTION_SUBTYPE,
  HEALTH_EVENT_SUBTYPE,
  SCHEDULED_AI_CHAT_REASON,
} from 'vd-constants';

const DEFAULT_EVENT_ICON_SIZE = 16;

interface EventIconProps {
  /** If specified, the desired height of the icon in pixels. */
  size?: number;
  style?: React.CSSProperties;
  dwellTime?: boolean;
  fill?: React.CSSProperties['fill'];
  isFutureEvent: boolean;
  state: IEvent['state'];
  subtype: IEvent['subtype'];
  type: IEvent['type'];
}

/**
 * Renders an icon for an event.
 * (e.g. renders a little running guy for "Motion" events.)
 */
const EventIcon: React.FC<EventIconProps> = (props) => {
  const { fill, isFutureEvent, state, style, subtype, type } = props;
  const size = props.size || DEFAULT_EVENT_ICON_SIZE;
  const locale = useLocale();

  // Common props for @mdi icons.
  const mdiProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: fill || 'var(--color-white)',
    style: {
      fontSize: size,
      ...style,
    },
  };

  const { icon: Icon } = getEventIconAndText({ type, subtype, state, isFutureEvent, locale });

  return <Icon {...mdiProps} />;
};

export default React.memo(EventIcon);

export function getEventIconAndText({
  type,
  subtype,
  state,
  isFutureEvent,
  locale,
}: {
  isFutureEvent: boolean;
  state: IEvent['state'];
  subtype: IEvent['subtype'];
  type: IEvent['type'];
  locale: LocaleDefinition;
}) {
  switch (type) {
    case EVENT_TYPE_MOTION:
      // Fill will be generated via CSS.
      return { icon: MotionIcon, text: locale.eventType.motion };
    case EVENT_TYPE_BOOKMARK:
      if (subtype === EVENT_SUBTYPE_ALARM) {
        if (state === EVENT_STATUS_OPEN) {
          return { icon: OpenAlarmIcon, text: locale.eventType.openAlarm };
        }
        return { icon: AlarmIcon, text: locale.eventType.alarm };
      } else if (subtype?.startsWith(EVENT_SUBTYPE_AI)) {
        if (subtype === DWELL_TIME_SUBTYPE) {
          return { icon: CarIcon, text: locale.eventType.dwellTime };
        } else if (subtype === EXIT_DOOR_OBSTRUCTION_SUBTYPE) {
          return { icon: PackageIcon, text: locale.eventType.blockedExit };
        }
        return { icon: RobotIcon, text: locale.eventType.ai };
      } else if (subtype === AGENT_SUBTYPE) {
        return { icon: AgentIcon, text: locale.eventType.agents };
      } else if (subtype === SCHEDULED_AI_CHAT_REASON) {
        return { icon: RobotIcon, text: locale.eventType.ai };
      } else if (subtype === EVENT_SUBTYPE_DOWNLOAD) {
        return { icon: DownloadIcon, text: locale.eventType.download };
      } else if (subtype === HEALTH_EVENT_SUBTYPE) {
        return { icon: HealthIcon, text: locale.eventType.health };
      } else if (subtype === EVENT_SUBTYPE_SCHEDULED) {
        if (state === EVENT_STATUS_CLOSED) {
          return { icon: CanceledScheduledIcon, text: locale.eventType.canceledScheduled };
        } else if (isFutureEvent) {
          return { icon: ScheduledIcon, text: locale.eventType.scheduled };
        }
        return { icon: PastScheduledIcon, text: locale.eventType.pastScheduled };
      }
      return { icon: BookmarkIcon, text: locale.eventType.bookmark };

    case EVENT_TYPE_TRANSACTION:
      if (subtype?.startsWith(DELIVERY_EVENT_SUBTYPE)) {
        return { icon: DeliveryIcon, text: locale.eventType.delivery };
      }
      return { icon: TransactionIcon, text: locale.eventType.transaction };

    case 'error':
    case undefined:
      return { icon: ErrorIcon, text: locale.eventType.error };
  }
}
